import { authedFetch } from '@/providers/loginWithEmail/dataProvider';

export type EstimateEmailDraft = {
    from_address: string;
    to: string;
    subject: string;
    body_html: string;
    related_people: Array<{ id: number; name: string; email: string }>;
};

export type SendQuoteEstimateEmailPayload = {
    to: string;
    subject: string;
    body_html: string;
};

export type SendQuoteEstimateEmailResult = {
    quote_id: number;
    recipient: string;
    subject: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseRecipientEmails(value: string): {
    valid: boolean;
    emails: string[];
    error?: string;
} {
    const emails = value.split(',').map(e => e.trim()).filter(Boolean);
    if (!emails.length) {
        return { valid: false, emails: [], error: 'At least one email is required' };
    }
    const invalid = emails.filter(e => !EMAIL_RE.test(e));
    if (invalid.length) {
        return {
            valid: false,
            emails,
            error: `Invalid email address${invalid.length > 1 ? 'es' : ''}: ${invalid.join(', ')}`,
        };
    }
    return { valid: true, emails };
}

function parseSendEmailError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;
    const errors = obj.errors;
    if (errors && typeof errors === 'object') {
        const root = (errors as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    const detail = obj.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

export async function fetchEstimateEmailDraft(quoteId: number): Promise<EstimateEmailDraft> {
    const res = await authedFetch(
        `/quotes/${quoteId}/estimate-email-draft`,
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseSendEmailError(data, 'Failed to load email draft'));
    }
    const draft = data as EstimateEmailDraft;
    return {
        ...draft,
        related_people: Array.isArray(draft.related_people) ? draft.related_people : [],
    };
}

export async function sendQuoteEstimateEmail(
    quoteId: number,
    payload: SendQuoteEstimateEmailPayload
): Promise<SendQuoteEstimateEmailResult> {
    const res = await authedFetch(
        `/quotes/${quoteId}/send-estimate-email`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseSendEmailError(data, 'Failed to send estimate email'));
    }
    return data as SendQuoteEstimateEmailResult;
}

export type MarkEstimateSentResult = {
    quote_id: number;
    assigned: boolean;
};

export async function markQuoteEstimateSent(
    quoteId: number
): Promise<MarkEstimateSentResult> {
    const res = await authedFetch(
        `/quotes/${quoteId}/mark-estimate-sent`,
        { method: 'POST' }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseSendEmailError(data, 'Failed to mark estimate as sent'));
    }
    return data as MarkEstimateSentResult;
}
